import React, { useState } from 'react';
import { 
  Drawer, 
  Tabs, 
  Button, 
  Input, 
  Space, 
  Tag, 
  Select, 
  InputNumber, 
  Table, 
  Popconfirm,
  message,
  Divider,
  Avatar,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faPlus,
  faTrash,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlannerPage, updatePlannerPage } from '../../../services/tasks';
import api from '../../../services/api';
import { getUsers, UserWithStats } from '../../../services/users';

const { TabPane } = Tabs;
const { Option } = Select;

interface ConfigurationPanelProps {
  plannerPage: PlannerPage;
  open: boolean;
  onClose: () => void;
}

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({ plannerPage, open, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('statuses');
  const [newStatus, setNewStatus] = useState('');
  const [newSwimlaneType, setNewSwimlaneType] = useState('priority');
  const [newSwimlaneValue, setNewSwimlaneValue] = useState('');
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [newTag, setNewTag] = useState('');
  const [newTagColor, setNewTagColor] = useState('#DB50AC');
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [editingSwimlaneIdx, setEditingSwimlaneIdx] = useState<number | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [dragOverSwimlaneIdx, setDragOverSwimlaneIdx] = useState<number | null>(null);
  
  // Get all unique tags from all tasks in this board
  const { data: allTasks = [] } = useQuery({
    queryKey: ['tasks', plannerPage.id],
    queryFn: () => api.get(`/api/tasks?plannerPageId=${plannerPage.id}`).then(res => res.data.data || []),
    enabled: open,
  });
  
  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    allTasks.forEach((task: any) => {
      if (task.tags && Array.isArray(task.tags)) {
        task.tags.forEach((tag: string) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [allTasks]);

  React.useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const updateMutation = useMutation({
    mutationFn: (data: any) => updatePlannerPage(plannerPage.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planner-pages'] });
      message.success('Configuration updated successfully');
    },
  });

  // Status Management
  const handleAddStatus = () => {
    const toAdd = newStatus
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && !plannerPage.statuses.includes(s.toLowerCase()));
    if (toAdd.length === 0) {
      setNewStatus('');
      return;
    }
    const added = toAdd.map((s) => s.toLowerCase());
    const updatedStatuses = [...plannerPage.statuses, ...added];
    updateMutation.mutate({ statuses: updatedStatuses });
    setNewStatus('');
  };

  const handleStatusRename = (oldStatus: string, newName: string) => {
    setEditingStatus(null);
    const trimmed = newName.trim().toLowerCase();
    if (!trimmed || trimmed === oldStatus) return;
    if (plannerPage.statuses.includes(trimmed)) return;
    const updatedStatuses = plannerPage.statuses.map((s) => (s === oldStatus ? trimmed : s));
    updateMutation.mutate({ statuses: updatedStatuses });
  };

  const handleRemoveStatus = (status: string) => {
    const updatedStatuses = plannerPage.statuses.filter(s => s !== status);
    updateMutation.mutate({ statuses: updatedStatuses });
    setEditingStatus(null);
  };

  const handleReorderStatus = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const list = [...plannerPage.statuses];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    updateMutation.mutate({ statuses: list });
    setDragOverStatus(null);
  };

  // WIP Limits Management
  const handleUpdateWipLimit = (status: string, limit: number | null) => {
    const wipLimits = { ...(plannerPage.wipLimits || {}) };
    if (limit === null || limit === 0) {
      delete wipLimits[status];
    } else {
      wipLimits[status] = limit;
    }
    updateMutation.mutate({ wipLimits });
  };

  // Swimlanes Management
  const handleAddSwimlane = () => {
    const toAdd = newSwimlaneValue
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && !(plannerPage.swimlanes || []).some((sl) => sl.type === newSwimlaneType && sl.value === s));
    if (toAdd.length === 0) {
      setNewSwimlaneValue('');
      return;
    }
    const swimlanes = [...(plannerPage.swimlanes || []), ...toAdd.map((value) => ({ type: newSwimlaneType, value }))];
    updateMutation.mutate({ swimlanes });
    setNewSwimlaneValue('');
  };

  const handleReorderSwimlane = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const list = [...(plannerPage.swimlanes || [])];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    updateMutation.mutate({ swimlanes: list });
    setDragOverSwimlaneIdx(null);
  };

  const handleRemoveSwimlane = (index: number) => {
    const swimlanes = [...(plannerPage.swimlanes || [])];
    swimlanes.splice(index, 1);
    updateMutation.mutate({ swimlanes });
    setEditingSwimlaneIdx(null);
  };

  const handleSwimlaneRename = (index: number, newValue: string) => {
    setEditingSwimlaneIdx(null);
    const trimmed = newValue.trim();
    if (!trimmed) return;
    const list = [...(plannerPage.swimlanes || [])];
    const item = list[index];
    if (!item || item.value === trimmed) return;
    list[index] = { ...item, value: trimmed };
    updateMutation.mutate({ swimlanes: list });
  };

  // Members Management
  const handleAddMember = async (userId: string) => {
    const currentMembers = plannerPage.members || [];
    if (!currentMembers.find(m => m.id === userId)) {
      const memberIds = [...currentMembers.map(m => m.id), userId];
      updateMutation.mutate({ memberIds });
    }
  };

  const handleRemoveMember = (userId: string) => {
    const memberIds = (plannerPage.members || [])
      .filter(m => m.id !== userId)
      .map(m => m.id);
    updateMutation.mutate({ memberIds });
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faGear} />
          <span>Board Configuration</span>
        </div>
      }
      placement="right"
      width={600}
      onClose={onClose}
      open={open}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Tags Tab */}
        <TabPane tab="Tags" key="tags">
          <div>
            <h3 style={{ color: '#F2F2F2', marginBottom: 16 }}>Tag Library</h3>
            <p style={{ color: '#BFBFBF', marginBottom: 16, fontSize: 12 }}>
              Manage tags used across tasks in this board. Tags help organize and filter tasks.
            </p>
            <div style={{ marginBottom: 16 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="Tag name"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  style={{ width: '70%' }}
                />
                <Input
                  type="color"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  style={{ width: '30%', height: 32 }}
                />
              </Space.Compact>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                {allTags.map((tag) => (
                  <Tag
                    key={tag}
                    color={newTagColor}
                    style={{ fontSize: 14, padding: '4px 12px', marginBottom: 8 }}
                  >
                    {tag}
                  </Tag>
                ))}
                {allTags.length === 0 && (
                  <span style={{ color: '#666', fontStyle: 'italic' }}>No tags yet</span>
                )}
              </Space>
            </div>
            <p style={{ color: '#666', fontSize: 11 }}>
              Note: Tags are currently free-form. This shows all tags used in tasks. 
              To remove a tag, edit tasks that use it.
            </p>
          </div>
        </TabPane>

        {/* Status Columns Tab */}
        <TabPane tab="Status Columns" key="statuses">
          <div>
            <h3 style={{ color: '#F2F2F2', marginBottom: 16 }}>Manage Status Columns</h3>
            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
              <Input
                placeholder="Add new status column (comma-separated for multiple)"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                onPressEnter={handleAddStatus}
              />
              <Button 
                type="primary" 
                icon={<FontAwesomeIcon icon={faPlus} />} 
                onClick={handleAddStatus}
                loading={updateMutation.isPending}
              >
                Add
              </Button>
            </Space.Compact>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {plannerPage.statuses.map((status, idx) => (
                editingStatus === status ? (
                  <Input
                    key={status}
                    size="small"
                    autoFocus
                    defaultValue={status}
                    style={{ width: 140 }}
                    onBlur={(e) => handleStatusRename(status, e.target.value)}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleStatusRename(status, (e.target as HTMLInputElement).value);
                    }}
                  />
                ) : (
                  <Tag
                    key={status}
                    closable
                    onClose={() => handleRemoveStatus(status)}
                    onClick={() => setEditingStatus(status)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', String(idx));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverStatus(status);
                    }}
                    onDragLeave={() => setDragOverStatus(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverStatus(null);
                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (!Number.isNaN(fromIdx)) handleReorderStatus(fromIdx, idx);
                    }}
                    style={{
                      fontSize: 14,
                      padding: '4px 8px',
                      cursor: 'pointer',
                      border: dragOverStatus === status ? '2px dashed #DB50AC' : undefined,
                    }}
                  >
                    {status}
                  </Tag>
                )
              ))}
            </div>
          </div>
        </TabPane>

        {/* WIP Limits Tab */}
        <TabPane tab="WIP Limits" key="wip">
          <div>
            <h3 style={{ color: '#F2F2F2', marginBottom: 16 }}>Set Work In Progress Limits</h3>
            <p style={{ color: '#BFBFBF', marginBottom: 24, fontSize: 12 }}>
              Limit the number of tasks that can be in each column to prevent bottlenecks.
            </p>
            <Table
              dataSource={plannerPage.statuses.map(status => ({
                key: status,
                status,
                limit: plannerPage.wipLimits?.[status] || null,
              }))}
              columns={[
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (text: string) => <span style={{ color: '#F2F2F2', textTransform: 'capitalize' }}>{text}</span>,
                },
                {
                  title: 'WIP Limit',
                  dataIndex: 'limit',
                  key: 'limit',
                  render: (limit: number | null, record: any) => (
                    <InputNumber
                      min={0}
                      placeholder="No limit"
                      value={limit}
                      onChange={(value) => handleUpdateWipLimit(record.status, value)}
                      style={{ width: 120 }}
                    />
                  ),
                },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        </TabPane>

        {/* Swimlanes Tab */}
        <TabPane tab="Swimlanes" key="swimlanes">
          <div>
            <h3 style={{ color: '#F2F2F2', marginBottom: 16 }}>Configure Swimlanes</h3>
            <p style={{ color: '#BFBFBF', marginBottom: 16, fontSize: 12 }}>
              Group tasks horizontally by priority, assignee, epic, or team.
            </p>
            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
              <Select
                value={newSwimlaneType}
                onChange={setNewSwimlaneType}
                style={{ width: '40%' }}
              >
                <Option value="priority">By Priority</Option>
                <Option value="assignee">By Assignee</Option>
                <Option value="epic">By Epic/Feature</Option>
                <Option value="team">By Team</Option>
              </Select>
              <Input
                placeholder="Swimlane value (comma-separated for multiple)"
                value={newSwimlaneValue}
                onChange={(e) => setNewSwimlaneValue(e.target.value)}
                onPressEnter={handleAddSwimlane}
                style={{ width: '60%' }}
              />
            </Space.Compact>
            <Button 
              type="primary" 
              icon={<FontAwesomeIcon icon={faPlus} />} 
              onClick={handleAddSwimlane}
              loading={updateMutation.isPending}
              style={{ marginBottom: 16 }}
            >
              Add Swimlane
            </Button>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(plannerPage.swimlanes || []).map((swimlane, idx) => (
                editingSwimlaneIdx === idx ? (
                  <Input
                    key={idx}
                    size="small"
                    autoFocus
                    defaultValue={swimlane.value}
                    style={{ width: 140 }}
                    onBlur={(e) => handleSwimlaneRename(idx, e.target.value)}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleSwimlaneRename(idx, (e.target as HTMLInputElement).value);
                    }}
                  />
                ) : (
                  <Tag
                    key={idx}
                    closable
                    onClose={() => handleRemoveSwimlane(idx)}
                    onClick={() => setEditingSwimlaneIdx(idx)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', String(idx));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverSwimlaneIdx(idx);
                    }}
                    onDragLeave={() => setDragOverSwimlaneIdx(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverSwimlaneIdx(null);
                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (!Number.isNaN(fromIdx)) handleReorderSwimlane(fromIdx, idx);
                    }}
                    style={{
                      fontSize: 14,
                      padding: '4px 8px',
                      cursor: 'pointer',
                      border: dragOverSwimlaneIdx === idx ? '2px dashed #DB50AC' : undefined,
                    }}
                  >
                    {swimlane.type}: {swimlane.value}
                  </Tag>
                )
              ))}
            </div>
          </div>
        </TabPane>

        {/* Members Tab */}
        <TabPane tab="Members" key="members">
          <div>
            <h3 style={{ color: '#F2F2F2', marginBottom: 16 }}>Board Members</h3>
            <div style={{ marginBottom: 16 }}>
              <Select
                placeholder="Add member to board"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                onSelect={handleAddMember}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users
                  .filter(user => !plannerPage.members?.find(m => m.id === user.id))
                  .map((user) => (
                    <Option key={user.id} value={user.id}>
                      <Space>
                        <Avatar size="small" src={user.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                        {user.name}
                      </Space>
                    </Option>
                  ))}
              </Select>
            </div>
            <Divider />
            <div>
              {plannerPage.members?.map((member) => (
                <div
                  key={member.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#1a1a1a',
                    borderRadius: 8,
                    marginBottom: 8,
                  }}
                >
                  <Space>
                    <Avatar src={member.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                    <span style={{ color: '#F2F2F2' }}>{member.name}</span>
                  </Space>
                  <Popconfirm
                    title="Remove member from board?"
                    onConfirm={() => handleRemoveMember(member.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<FontAwesomeIcon icon={faTrash} />}
                      size="small"
                    >
                      Remove
                    </Button>
                  </Popconfirm>
                </div>
              ))}
            </div>
          </div>
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default ConfigurationPanel;

